import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, computed,
  CUSTOM_ELEMENTS_SCHEMA, effect,
  ElementRef, Inject,
  inject, OnDestroy,
  OnInit, PLATFORM_ID, signal,
  ViewChild
} from '@angular/core';
import {HeaderComponent} from "../../common/header/header.component";
import {
  IonButton, IonButtons,
  IonCol,
  IonContent, IonFooter,
  IonIcon,
  IonPopover,
  IonRow, IonSpinner, IonToolbar, ToastController
} from "@ionic/angular/standalone";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {CurrencyPipe, DecimalPipe, isPlatformBrowser} from "@angular/common";
import {PropertyCardComponent} from "../common/property-card/property-card.component";
import {FooterComponent} from "../../common/footer/footer.component";
import {PropertyService} from "../property.service";
import {LanguageService} from "../../core/language.service";
import {environment} from "../../../environments/environment";
// Google Maps removed - not used on property page
import {YouTubePlayer} from "@angular/youtube-player";
import Swiper from "swiper";
import {PriceCardComponent} from './price-card/price-card.component';
import {MeetUsInPersonComponent} from './meet-us-in-person/meet-us-in-person.component';
import {MeetOurTeamComponent} from './meet-our-team/meet-our-team.component';
import {PropertyUnitsComponent} from './property-units/property-units.component';
import {whatsAppNumberFloating} from '../../configs/content/contact-details-en';
import {ParentPropertyComponent} from './parent-property/parent-property.component';
import {PropertyImageGalleryComponent} from './property-image-gallery/property-image-gallery.component';
import {B2bSectionComponent} from '../../pages/components/b2b-section/b2b-section.component';
import {Meta, Title} from '@angular/platform-browser';
import {ShareButtonDirective} from 'ngx-sharebuttons';
import {CurrencyConvertService} from '../../core/currency.convert.service';
import {AuthService} from '../../auth/auth.service';
import {WishlistService, WishlistExists} from '../wishlist.service';
import {HttpErrorResponse} from '@angular/common/http';
import {Property} from '../property-interface';
import {TranslateModule} from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-property-page',
    templateUrl: './property-page.component.html',
    styleUrls: ['./property-page.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      TranslateModule,
      HeaderComponent,
      IonContent,
      IonRow,
      IonCol,
      IonIcon,
      PropertyCardComponent,
      FooterComponent,
      DecimalPipe,
      PriceCardComponent,
      MeetUsInPersonComponent,
      MeetOurTeamComponent,
      IonFooter,
      IonToolbar,
      IonButton,
      CurrencyPipe,
      RouterLink,
      PropertyUnitsComponent,
      ParentPropertyComponent,
      PropertyImageGalleryComponent,
      B2bSectionComponent,
      IonButtons,
      IonPopover,
      ShareButtonDirective,
      IonSpinner
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PropertyPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('thumbsSwiper', { static: false}) thumbsSwiper: ElementRef | any;
  @ViewChild('thumbSwiper2', { static: false}) thumbSwiper2: ElementRef | any;
  @ViewChild('ytPlayer', { static: false}) ytPlayer!: YouTubePlayer;

  propertyService: PropertyService = inject(PropertyService);
  languageService: LanguageService = inject(LanguageService);
  authService: AuthService = inject(AuthService);
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  toastController: ToastController = inject(ToastController);

  properties: any = [];
  // Google Maps removed - coordinates kept for directions link
  coordinates: { lat: number, lng: number } = { lng: 46.827841, lat: 24.818226};
  cds: ChangeDetectorRef = inject(ChangeDetectorRef);
  swiper: Swiper | undefined;
  showMore: boolean = false;
  footerExpanded: boolean = false;
  currencyConvertService: CurrencyConvertService = inject(CurrencyConvertService);
  userService: AuthService = inject(AuthService);
  wishlistService: WishlistService = inject(WishlistService);
  isUpdated = false;

  // Meta management
  private currentMetaTags: string[] = [];

  constructor(private title: Title, private meta: Meta, @Inject(PLATFORM_ID) private platformId: any) {
    effect(async () => {
      // Handle property data updates
      if (this.property() && this.property()._id) {
        await this.handlePropertyData();
      }

      // Handle errors
      if (this.propertyError()) {
        await this.router.navigate(['/not-found']);
        return;
      }

      // Handle user authentication
      if (this.userService.userSignal()) {
        const currentUser = this.userService.userSignal();
        if (currentUser && currentUser._id) {
          this.userModel.set(currentUser._id);
        }
      }

      this.isUpdated = true;
    });

    // Separate effect for meta data to ensure it runs immediately
    effect(() => {
      const metaData = this.metaData();
      if (metaData && metaData._id) {
        this.setMetaTags(metaData);
      }
    });
  }

  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }


  private setMetaTags(metaData: any): void {
    try {
      if (!metaData || !metaData._id) return;

      // Clear existing meta tags
      this.clearCurrentMetaTags();

      // Set page title
      const pageTitle = metaData.titleEn || 'Property - Satel Real Estate';
      this.title.setTitle(pageTitle);

      // Get optimized image URL
      const imageUrl = this.getOptimizedImageUrl(metaData.image);
      const currentUrl = `https://satel.sa/p/${metaData._id}`;
      const description = this.sanitizeDescription(metaData.metaDescription);

      // Add meta tags one by one to avoid type issues
      // Basic meta tags
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ name: 'keywords', content: `${pageTitle}, real estate, property, Satel, Saudi Arabia` });

      // Open Graph meta tags
      this.meta.updateTag({ property: 'og:title', content: pageTitle });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ property: 'og:image', content: imageUrl });
      this.meta.updateTag({ property: 'og:image:secure_url', content: imageUrl });
      this.meta.updateTag({ property: 'og:image:width', content: '1200' });
      this.meta.updateTag({ property: 'og:image:height', content: '630' });
      this.meta.updateTag({ property: 'og:image:type', content: 'image/jpeg' });
      this.meta.updateTag({ property: 'og:image:alt', content: `${pageTitle} - Property Image` });
      this.meta.updateTag({ property: 'og:url', content: currentUrl });
      this.meta.updateTag({ property: 'og:type', content: 'website' });
      this.meta.updateTag({ property: 'og:site_name', content: 'Satel Real Estate' });
      this.meta.updateTag({ property: 'og:locale', content: 'en_US' });
      this.meta.updateTag({ property: 'og:locale:alternate', content: 'ar_SA' });

      // Twitter meta tags
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.meta.updateTag({ name: 'twitter:site', content: '@satel_sa' });
      this.meta.updateTag({ name: 'twitter:creator', content: '@satel_sa' });
      this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
      this.meta.updateTag({ name: 'twitter:description', content: description });
      this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
      this.meta.updateTag({ name: 'twitter:image:alt', content: `${pageTitle} - Property Image` });

      // WhatsApp and additional tags
      this.meta.updateTag({ property: 'whatsapp:image', content: imageUrl });
      this.meta.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large' });
      this.meta.updateTag({ name: 'googlebot', content: 'index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1' });

      // Store current meta tags for cleanup (simplified)
      this.currentMetaTags = [
        'name="description"',
        'name="keywords"',
        'property="og:title"',
        'property="og:description"',
        'property="og:image"',
        'property="og:image:secure_url"',
        'property="og:url"',
        'name="twitter:title"',
        'name="twitter:description"',
        'name="twitter:image"',
        'property="whatsapp:image"'
      ];

      console.log('Meta tags set successfully for:', pageTitle);
    } catch (error) {
      console.error('Error setting meta tags:', error);
    }
  }

  private clearCurrentMetaTags(): void {
    // Use removeTag with the selector format that works
    const selectorsToRemove = [
      'name="description"',
      'name="keywords"',
      'property="og:title"',
      'property="og:description"',
      'property="og:image"',
      'property="og:image:secure_url"',
      'property="og:url"',
      'name="twitter:title"',
      'name="twitter:description"',
      'name="twitter:image"',
      'property="whatsapp:image"'
    ];

    selectorsToRemove.forEach(selector => {
      try {
        this.meta.removeTag(selector);
      } catch (e) {
        // Ignore errors if tag doesn't exist
      }
    });
  }

  private getOptimizedImageUrl(imageUrl?: string): string {
    if (!imageUrl) {
      return 'https://satel.sa/assets/satel-og-image.jpg'; // Default OG image
    }

    // Ensure absolute URL
    let fullImageUrl = imageUrl;
    if (!imageUrl.startsWith('http')) {
      fullImageUrl = `https://satel.sa${imageUrl}`;
    }

    // Add optimization parameters if it's from your CDN
    if (fullImageUrl.includes('satel-resources.nyc3.digitaloceanspaces.com')) {
      const separator = fullImageUrl.includes('?') ? '&' : '?';
      return `${fullImageUrl}${separator}w=1200&h=630&fit=crop&q=85&f=auto`;
    }

    return fullImageUrl;
  }

  private sanitizeDescription(description?: string): string {
    if (!description) {
      return 'Discover premium properties with Satel Real Estate. Find your perfect home in Saudi Arabia with our expert guidance and comprehensive property listings.';
    }

    // Remove HTML tags and clean up
    const cleaned = description
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Ensure optimal length for social sharing (120-160 characters)
    if (cleaned.length > 160) {
      return cleaned.substring(0, 157) + '...';
    } else if (cleaned.length < 120) {
      return cleaned + ' | Premium Properties by Satel Real Estate';
    }

    return cleaned;
  }

  private async handlePropertyData(): Promise<void> {
    const property = this.property();

    // Check post status
    if (property.postStatus && property.postStatus !== 'Published' && property.postStatus !== 'Draft') {
      await this.router.navigate(['/not-found']);
      return;
    }

    // Handle currency conversion
    if (this.currencyConvertService.localCurrency()) {
      property.newPrice = property.price * this.currencyConvertService.exchangeRates[this.currencyConvertService.localCurrency()];
    }

    // Load related properties
    this.properties = await this.propertyService.getProperties({limit: 4});

    // Set map coordinates
    if (property.address && property.address.location) {
      this.coordinates = { lng: property.address.lng, lat: property.address.lat };
    }
  }

  // Component signals and resources
  propertyModel = signal<string>('');
  propertyId = signal<string>('');
  userModel = signal<string>('');

  private wishlistResource = this.wishlistService.getWishlist(this.userModel, this.propertyModel);
  isWishlistExists = computed(() => this.wishlistResource.value() ?? {} as WishlistExists);
  isWishlistExistsError = computed(() => this.wishlistResource.error() as HttpErrorResponse);
  isWishlistLoading = this.wishlistResource.isLoading;

  private metaResource = this.propertyService.getMeta(this.propertyId);
  private propertyResource = this.propertyService.getPropertyResource(this.propertyId);
  metaData = computed(() => this.metaResource.value() ?? {} as Property);
  property = computed(() => this.propertyResource.value() ?? {} as Property);
  propertyError = computed(() => this.propertyResource.error() as HttpErrorResponse);
  isPropertyLoading = computed(() => this.propertyResource.isLoading);

  parentId: string = "";
  parentName: string = "";
  inWishList = signal(false);

  async updateWishList(id: string) {
    try {
      this.propertyId.set(this.property()._id);
      this.inWishList.update(v => !v);
      const wishlist: any = await this.userService.updateWishlist(id);
    } catch (e) {
      console.error('Error updating wishlist:', e);
    }
  }

  async ngOnInit() {
    this.route.params.subscribe(async (params: any) => {
      if (params.id) {
        this.propertyModel.set(params.id);
        this.propertyId.set(params.id);
      }
    });

    this.route.queryParams.subscribe((params) => {
      if (params && params['parentId']) {
        this.parentId = params['parentId'];
        this.parentName = params['parentName'];
      }
    });
  }

  isContentLoaded = false;
  async ngAfterViewInit() {
    await this.loadSwiper();
    this.isContentLoaded = true;
    if (this.thumbsSwiper) {
      this.thumbsSwiper?.nativeElement.swiper.reload();
      this.thumbSwiper2?.nativeElement.swiper.reload();
      this.thumbsSwiper.nativeElement.initialize();
      setTimeout(() => {
        this.thumbSwiper2.nativeElement.initialize();
      }, 1000);
    }
  }

  async loadSwiper() {
    const { register } = await import("swiper/element/bundle");
    register();
  }

  ionViewDidLeave() {
    if (this.ytPlayer) {
      this.ytPlayer.pauseVideo();
    }
  }

  ngOnDestroy() {
    // Clean up meta tags when component is destroyed
    this.clearCurrentMetaTags();
  }

  // Utility methods
  openGoogleMapsDirections(coordinates: { lat: number, lng: number }) {
    const origin = encodeURIComponent('');
    const destination = encodeURIComponent(coordinates.lat + ',' + coordinates.lng);
    const travelMode = 'driving';
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=${travelMode}`;
    window.open(url, '_blank');
  }

  @ViewChild('popover') popover!: IonPopover;

  generateUrl() {
    return `https://satel.sa${this.router.url}`;
  }

  copyUrl() {
    navigator.clipboard.writeText(this.generateUrl());
    this.showToast();
    this.popover.dismiss();
  }

  async showToast() {
    const toast = await this.toastController.create({
      message: 'URL copied to clipboard',
      duration: 3000,
      position: 'top',
    });
    await toast.present();
  }

  protected readonly whatsAppNumberFloating = whatsAppNumberFloating;
}
